import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import config from '../config';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Where to go after signing in or registering: the page that sent the user there (path + query +
// hash, so the OAuth consent page keeps its ?request= id), or home. Internal paths only.
export const returnPath = (from) => {
  if (!from || typeof from.pathname !== 'string' || !from.pathname.startsWith('/') || from.pathname.startsWith('//')) {
    return '/';
  }
  return `${from.pathname}${from.search || ''}${from.hash || ''}`;
};

const TOKEN_KEY = 'authToken';

const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common['Authorization'];
  }
};

// Returns { token, decoded } for a stored, unexpired session token, else null.
const readStoredSession = () => {
  let token = null;
  try {
    token = localStorage.getItem(TOKEN_KEY);
  } catch (e) {
    return null;
  }
  if (!token) return null;
  try {
    const decoded = jwtDecode(token);
    if (decoded && decoded.exp * 1000 > Date.now()) return { token, decoded };
  } catch (e) {
    // Malformed token: treated as signed out below.
  }
  return null;
};

// Send the stored session with every axios request from the very first render. Child components'
// effects run before AuthProvider's, so waiting for the provider would race their first fetches.
(() => {
  const session = readStoredSession();
  if (session) axios.defaults.headers.common['Authorization'] = `Bearer ${session.token}`;
})();

// Endpoints whose 401 means "wrong credentials", not "session expired": never refresh on them.
const isAuthEndpoint = (url = '') => /\/(token|refresh-token|register|google-auth)(\?|$)/.test(url);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshPromise = useRef(null);

  useEffect(() => {
    console.log('AuthContext error state changed:', error);
  }, [error]);

  const logout = useCallback(() => {
    setAuthToken(null);
    setUser(null);
    setError(null);
    console.log('Logged out successfully');
    localStorage.setItem('app-logout', Date.now().toString());
  }, []);

  // Adopt a session token obtained elsewhere (e.g. /token right after registration).
  const loginWithToken = useCallback((token) => {
    const decoded = jwtDecode(token);
    setAuthToken(token);
    setUser(decoded);
    setError(null);
    return decoded;
  }, []);

  // Single-flight: concurrent callers share one /refresh-token request.
  const refreshToken = useCallback(() => {
    if (refreshPromise.current) return refreshPromise.current;
    const current = localStorage.getItem(TOKEN_KEY);
    const run = (async () => {
      if (!current) throw new Error('No session to refresh');
      try {
        const response = await axios.post(`${config.REACT_APP_API_URL}/refresh-token`, {}, {
          headers: { Authorization: `Bearer ${current}` },
          _skipAuthRefresh: true,
        });
        const { access_token: token } = response.data || {};
        if (!token) throw new Error('Refresh response did not include an access token');
        setAuthToken(token);
        setUser(jwtDecode(token));
        console.log('Token refreshed successfully');
        return token;
      } catch (err) {
        console.error('Error refreshing token:', err);
        logout();
        throw err;
      } finally {
        refreshPromise.current = null;
      }
    })();
    refreshPromise.current = run;
    return run;
  }, [logout]);

  useEffect(() => {
    const checkAuthState = () => {
      const session = readStoredSession();
      if (session) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${session.token}`;
        setUser(session.decoded);
      } else {
        setUser(null);
        if (localStorage.getItem(TOKEN_KEY)) setAuthToken(null);
        else delete axios.defaults.headers.common['Authorization'];
      }
      setLoading(false);
    };

    checkAuthState();

    const handleStorageChange = (e) => {
      if (e.key === TOKEN_KEY || e.key === 'app-logout') {
        checkAuthState();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Refresh a minute before the session expires.
  useEffect(() => {
    if (user) {
      const refreshIn = Math.max(user.exp * 1000 - Date.now() - 60 * 1000, 0);
      const refreshTimer = setTimeout(() => {
        refreshToken().catch(() => {});
      }, refreshIn);
      return () => clearTimeout(refreshTimer);
    }
  }, [user, refreshToken]);

  // On a 401 from a session-authenticated request, refresh once and retry it.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        const shouldRefresh =
          error.response &&
          error.response.status === 401 &&
          !originalRequest._retry &&
          !originalRequest._skipAuthRefresh &&
          !isAuthEndpoint(originalRequest.url) &&
          !!localStorage.getItem(TOKEN_KEY);
        if (!shouldRefresh) {
          return Promise.reject(error);
        }
        originalRequest._retry = true;
        try {
          const accessToken = await refreshToken();
          if (originalRequest.headers && typeof originalRequest.headers.set === 'function') {
            originalRequest.headers.set('Authorization', `Bearer ${accessToken}`);
          } else {
            originalRequest.headers = { ...(originalRequest.headers || {}), Authorization: `Bearer ${accessToken}` };
          }
          return axios(originalRequest);
        } catch (refreshError) {
          // refreshToken() already logged out; surface the original 401 to the caller.
          return Promise.reject(error);
        }
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [refreshToken]);

  const login = async (username, password) => {
    console.log('API URL:', config.REACT_APP_API_URL);
    console.log('Attempting login');
    try {
      const formData = new URLSearchParams();
      formData.append('username', username);
      formData.append('password', password);

      const response = await axios.post(`${config.REACT_APP_API_URL}/token`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      console.log('Login response:', response);

      const { access_token } = response.data;
      loginWithToken(access_token);

      console.log('Login successful');
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'An error occurred during login. Please try again.';
      if (error.response) {
        console.log('Error response:', error.response);
        if (error.response.status === 401) {
          errorMessage = 'Invalid username or password. Please try again.';
        } else if (error.response.status === 400 && error.response.data && error.response.data.detail === 'Inactive user') {
          // /token refuses disabled accounts; say so in the same words as the account pages.
          errorMessage = 'This Everscope account is disabled.';
        } else if (error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      }
      console.log('Setting error:', errorMessage);
      setError(errorMessage);
      setUser(null);
      throw error
    }
  };

  const clearError = useCallback(() => {
    console.log('Clearing error');
    setError(null);
  }, []);

  const value = {
    user,
    login,
    loginWithToken,
    logout,
    refreshToken,
    loading,
    error,
    clearError,
    isAuthenticated: () => !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
